import { Pipe, PipeTransform } from '@angular/core';

/**
 * Frontend-only readability formatting:
 * - Adds line breaks after sentence endings when the text is a long running paragraph.
 * - Tries to avoid breaking on common abbreviations and decimals.
 * - Does NOT add/remove words or change facts.
 */
@Pipe({
  name: 'soapSentenceBreaks',
  standalone: true,
  pure: true
})
export class SoapSentenceBreaksPipe implements PipeTransform {
  transform(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const text = value.replace(/\r/g, '');

    // If it's already formatted with multiple lines, leave it alone.
    const newlineCount = (text.match(/\n/g) ?? []).length;
    if (newlineCount >= 3) {
      return text;
    }

    // Only apply when it's actually long enough to benefit.
    if (text.length < 250) {
      return text;
    }

    return this.insertSentenceBreaks(text);
  }

  private insertSentenceBreaks(text: string): string {
    const stopWords = new Set([
      'dr',
      'mr',
      'mrs',
      'ms',
      'prof',
      'sr',
      'jr',
      'vs',
      'etc',
      'eg',
      'ie',
      'no',
      'st',
      'mg',
      'ml',
      'kg',
      'lb',
      'oz',
      'hr',
      'min',
      'sec',
      'jan',
      'feb',
      'mar',
      'apr',
      'may',
      'jun',
      'jul',
      'aug',
      'sep',
      'sept',
      'oct',
      'nov',
      'dec',
      'am',
      'pm'
    ]);

    const paragraphStarters = new Set([
      'she',
      'he',
      'they',
      'patient',
      'the',
      'this',
      'overall',
      'additionally',
      'also',
      'however',
      'moreover',
      'furthermore',
      'in',
      'on',
      'at',
      'today',
      'currently'
    ]);

    const isUpperShortToken = (token: string) =>
      token.length <= 2 && token.toUpperCase() === token && /[A-Z]/.test(token);

    const prevToken = (s: string, endIdxInclusive: number): string => {
      let i = endIdxInclusive;
      // skip non-letters/digits at end
      while (i >= 0 && !/[A-Za-z0-9]/.test(s[i])) {
        i--;
      }
      let end = i;
      while (i >= 0 && /[A-Za-z0-9]/.test(s[i])) {
        i--;
      }
      const token = s.slice(i + 1, end + 1);
      return token;
    };

    const nextWordLower = (s: string, startIdx: number): string => {
      let i = startIdx;
      while (i < s.length && /[^A-Za-z]/.test(s[i])) {
        i++;
      }
      const start = i;
      while (i < s.length && /[A-Za-z]/.test(s[i])) {
        i++;
      }
      return s.slice(start, i).toLowerCase();
    };

    let out = '';
    const n = text.length;

    for (let i = 0; i < n; i++) {
      const ch = text[i];
      out += ch;

      if (ch !== '.' && ch !== '?' && ch !== '!') {
        continue;
      }

      // Don't break decimals like "1.5" or "2026.1"
      const prevChar = i > 0 ? text[i - 1] : '';
      const nextChar = i + 1 < n ? text[i + 1] : '';
      if (/\d/.test(prevChar) && /\d/.test(nextChar)) {
        continue;
      }

      // Lookahead: we only break when there's whitespace and then an uppercase letter.
      let j = i + 1;
      let sawSpace = false;
      while (j < n && text[j] === ' ') {
        sawSpace = true;
        j++;
      }
      if (!sawSpace || j >= n) {
        continue;
      }

      const nextNonSpace = text[j];
      if (!/[A-Z]/.test(nextNonSpace)) {
        continue;
      }

      const token = prevToken(text, i - 1);
      const tokenLower = token.toLowerCase();
      if (stopWords.has(tokenLower) || isUpperShortToken(token)) {
        continue;
      }

      // Replace the spaces after punctuation with a newline.
      // If the next sentence looks like a new paragraph start, insert a blank line.
      const w = nextWordLower(text, j);
      out += paragraphStarters.has(w) ? '\n\n' : '\n';
      i = j - 1; // skip the spaces we've consumed
    }

    return out;
  }
}

